import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { Button } from "./ui/button";

import { planningAPI } from "../services/planningService";
import { employeesAPI } from "../services/api";
import OverrideSummaryCards from "./schedule/OverrideSummaryCards";
import OverrideList from "./schedule/OverrideList";
import OverrideCreateDialog from "./schedule/OverrideCreateDialog";
import OverrideEditDialog from "./schedule/OverrideEditDialog";
import OverrideDeleteDialog from "./schedule/OverrideDeleteDialog";

const emptyForm = (propEmployeeId) => ({
  employee_id: propEmployeeId || "",
  date: "",
  override_schedule_id: "",
  reason: "",
  is_exceptional: false,
  priority: 5,
});

const ScheduleOverrideRequest = ({ employeeId: propEmployeeId = null, onRequestCreated = null }) => {
  const { t } = useTranslation();
  const [overrides, setOverrides] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedOverride, setSelectedOverride] = useState(null);
  const [summary, setSummary] = useState(null);
  const [formData, setFormData] = useState(emptyForm(propEmployeeId));

  useEffect(() => { loadData(); }, [propEmployeeId]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([loadOverrides(), loadSchedules(), loadEmployees(), loadSummary()]);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadOverrides = async () => {
    try {
      const filters = propEmployeeId ? { employee_id: propEmployeeId } : {};
      const data = await planningAPI.overrides.getScheduleOverrides(filters);
      setOverrides(data.items || []);
    } catch (error) {
    }
  };

  const loadSchedules = async () => {
    try {
      const data = await planningAPI.schedules.getSchedules({ is_active: true, limit: 100 });
      setSchedules(data.items || []);
    } catch (error) {
    }
  };

  const loadEmployees = async () => {
    try {
      if (!propEmployeeId) {
        const data = await employeesAPI.getEmployees({ limit: 1000 });
        setEmployees(data.employees || []);
      }
    } catch (error) {
    }
  };

  const loadSummary = async () => {
    try {
      const filters = propEmployeeId ? { employee_id: propEmployeeId } : {};
      const data = await planningAPI.overrides.getOverridesSummary(filters);
      setSummary(data);
    } catch (error) {
    }
  };

  const resetForm = () => setFormData(emptyForm(propEmployeeId));

  const handleOpenCreateModal = () => { resetForm(); setShowCreateModal(true); };

  const handleOpenEditModal = (override) => {
    setSelectedOverride(override);
    setFormData({
      employee_id: override.employee_id,
      date: override.date,
      override_schedule_id: override.override_schedule_id,
      reason: override.reason || "",
      is_exceptional: override.is_exceptional || false,
      priority: override.priority || "MEDIUM",
    });
    setShowEditModal(true);
  };

  const handleOpenDeleteDialog = (override) => {
    setSelectedOverride(override);
    setShowDeleteDialog(true);
  };

  const handleCreateRequest = async () => {
    try {
      if (!formData.employee_id) { toast.error(t("planning.legacy.overrides.errors.employeeRequired")); return; }
      if (!formData.date) { toast.error(t("planning.legacy.overrides.errors.dateRequired")); return; }
      if (!formData.override_schedule_id) { toast.error(t("planning.legacy.overrides.errors.scheduleRequired")); return; }
      if (!formData.reason.trim()) { toast.error(t("planning.legacy.overrides.errors.reasonRequired")); return; }

      await planningAPI.overrides.createScheduleOverride(formData);
      await loadData();
      setShowCreateModal(false);
      resetForm();
      if (onRequestCreated) onRequestCreated();
    } catch (error) {
    }
  };

  const handleUpdateRequest = async () => {
    try {
      if (!formData.reason.trim()) { toast.error(t("planning.legacy.overrides.errors.reasonRequired")); return; }
      const updateData = {
        override_schedule_id: formData.override_schedule_id,
        reason: formData.reason,
        is_exceptional: formData.is_exceptional,
        priority: formData.priority,
      };
      await planningAPI.overrides.updateScheduleOverride(selectedOverride.id, updateData);
      await loadData();
      setShowEditModal(false);
      setSelectedOverride(null);
      resetForm();
    } catch (error) {
    }
  };

  const handleDeleteRequest = async () => {
    try {
      await planningAPI.overrides.deleteScheduleOverride(selectedOverride.id);
      await loadData();
      setShowDeleteDialog(false);
      setSelectedOverride(null);
    } catch (error) {
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          <p className="mt-2 text-sm text-muted-foreground">{t("planning.legacy.overrides.loading")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">{t("planning.legacy.overrides.title")}</h3>
          <p className="text-sm text-muted-foreground">{t("planning.legacy.overrides.subtitle")}</p>
        </div>
        <Button onClick={handleOpenCreateModal}>
          <Send className="h-4 w-4 mr-2" /> {t("planning.legacy.overrides.new")}
        </Button>
      </div>

      <OverrideSummaryCards summary={summary} />

      <OverrideList
        overrides={overrides}
        propEmployeeId={propEmployeeId}
        employees={employees}
        schedules={schedules}
        handleOpenEditModal={handleOpenEditModal}
        handleOpenDeleteDialog={handleOpenDeleteDialog}
      />

      <OverrideCreateDialog
        open={showCreateModal}
        onOpenChange={setShowCreateModal}
        propEmployeeId={propEmployeeId}
        formData={formData}
        setFormData={setFormData}
        employees={employees}
        schedules={schedules}
        handleCreateRequest={handleCreateRequest}
      />

      <OverrideEditDialog
        open={showEditModal}
        onOpenChange={setShowEditModal}
        formData={formData}
        setFormData={setFormData}
        schedules={schedules}
        handleUpdateRequest={handleUpdateRequest}
      />

      <OverrideDeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        handleDeleteRequest={handleDeleteRequest}
      />
    </div>
  );
};

export default ScheduleOverrideRequest;
